import {FormControl, FormField} from 'platform/components';
import {GridItem, Grid, Space, VStack, Show} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {SeriesResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getOptionsFromSeries, SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {useDocumentTemplates} from '@dms/shared';

import {suffixTestId, RequiredTestIdProps, Nullish} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';

interface DocumentsTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId: string | Nullish;
  seriesList: SeriesResponseBody[] | Nullish;
}

export function DocumentsTab(props: DocumentsTabProps) {
  const [getTemplates] = useDocumentTemplates();

  const isFastTrackEnabled = useWatch({
    control: props.control,
    name: 'documents.fastTrackIssue.isEnable',
  });

  const {options: offerTemplateOptions} = getTemplates('direct-sale-offer-html');
  const {options: fastTrackTemplateOptions} = getTemplates('workshop-fast_track');

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <Grid columns={2}>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.docSeriesId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.offerSeries')}
                options={getOptionsFromSeries(props.seriesList, 'warehouse/offer')}
                data-testid={suffixTestId('offerSeriesId', props)}
              />
            </GridItem>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.orderOffer.templateId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.offerTemplate')}
                options={offerTemplateOptions}
                data-testid={suffixTestId('offerTemplateId', props)}
              />
            </GridItem>
            <GridItem span={1}>
              <FormField
                control={props.control}
                name="documents.fastTrackIssue.isEnable"
                type="switch"
                label={i18n.t('entity.warehouse.labels.enableFastTrackIssue')}
                data-testid={suffixTestId('isFastTrackEnabled', props)}
              />
            </GridItem>
            <GridItem span={1} />
            <Show when={isFastTrackEnabled}>
              <GridItem span={1}>
                <FormField
                  isRequired
                  control={props.control}
                  name="documents.fastTrackIssue.docSeriesId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.fastTrackSeries')}
                  options={getOptionsFromSeries(props.seriesList, 'warehouse/fast-track')}
                  data-testid={suffixTestId('fastTrackDocSeriesId', props)}
                />
              </GridItem>
              <GridItem span={1}>
                <FormField
                  control={props.control}
                  name="documents.fastTrackIssue.templateId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.fastTrackTemplate')}
                  options={fastTrackTemplateOptions}
                  data-testid={suffixTestId('fastTrackTemplateId', props)}
                />
              </GridItem>
            </Show>
          </Grid>
        </VStack>
      </SettingsSection>

      <Space vertical={18} />

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </>
  );
}
